/**
 * AssistantDevPanel — „Code / Entwicklung"-Ansicht (mehrere Projekte).
 *
 * Zeigt Status, Fortschrittsbalken, voraussichtliche Online-Zeit (deutsche Zeit) und Bereich.
 * Bei „Rückfrage" erklärt der Assistent die Frage + Warum und liefert antippbare Antwort-Vorschläge.
 */

import { useEffect, useMemo, useState } from 'react';
import { X, Code2, Loader2, ExternalLink, RefreshCw, MessageSquare, Send } from 'lucide-react';
import { supabase } from '@/lib/supabase';

async function apiPost(body: any) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  const res = await fetch('/api/assistant-devstatus', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || ''}` },
    body: JSON.stringify(body),
  });
  return res.json().catch(() => ({}));
}

type Eintrag = {
  nummer: number; titel: string; art: string; repo: string; bereich: string;
  status: string; fortschritt: number; voraussichtlich: string; kann_antworten?: boolean; prioritaet?: string; typ?: string; url: string;
};

const STATUS_STYLE: Record<string, string> = {
  'Rückfrage': 'bg-amber-500/20 text-amber-300',
  'Bereit zum Merge': 'bg-emerald-500/20 text-emerald-300',
  'Review': 'bg-[#6d8bff]/20 text-[#a9bcff]',
  'Nachbesserung': 'bg-orange-500/20 text-orange-300',
  'Warteschlange': 'bg-white/10 text-slate-300',
  'Fertig': 'bg-slate-500/20 text-slate-400',
};
const BAR_COLOR: Record<string, string> = {
  'Rückfrage': 'bg-amber-400', 'Bereit zum Merge': 'bg-emerald-400', 'Review': 'bg-[#6d8bff]',
  'Nachbesserung': 'bg-orange-400', 'Warteschlange': 'bg-slate-400', 'Fertig': 'bg-slate-500',
};

export default function AssistantDevPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Eintrag[]>([]);
  const [bereiche, setBereiche] = useState<string[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [aktualisiert, setAktualisiert] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('Alle');

  const [replyKey, setReplyKey] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyMsg, setReplyMsg] = useState<string | null>(null);
  const [detailsBusy, setDetailsBusy] = useState(false);
  const [details, setDetails] = useState<{ frage?: string; warum?: string; vorschlaege?: { text: string; empfohlen?: boolean }[] } | null>(null);

  async function laden() {
    setLoading(true); setHinweis(null);
    const d = await apiPost({ action: 'list' });
    setItems(Array.isArray(d?.eintraege) ? d.eintraege : []);
    setBereiche(Array.isArray(d?.bereiche) ? d.bereiche : []);
    setSummary(d?.zusammenfassung || null);
    setAktualisiert(d?.aktualisiert || null);
    setHinweis(d?.hinweis || d?.error || null);
    setLoading(false);
  }

  useEffect(() => { if (open) laden(); /* eslint-disable-next-line */ }, [open]);

  async function oeffneAntwort(e: Eintrag, key: string) {
    setReplyKey(key); setReplyText(''); setReplyMsg(null); setDetails(null); setDetailsBusy(true);
    const d = await apiPost({ action: 'rueckfrage_details', repo: e.repo, nummer: e.nummer });
    setDetails(d && !d.error ? d : null);
    setDetailsBusy(false);
  }

  async function antworten(e: Eintrag) {
    if (!replyText.trim() || replyBusy) return;
    setReplyBusy(true); setReplyMsg(null);
    const d = await apiPost({ action: 'antworten', repo: e.repo, nummer: e.nummer, text: replyText.trim() });
    if (d?.ok) { setReplyKey(null); setReplyText(''); setReplyMsg(null); setDetails(null); await laden(); }
    else setReplyMsg(d?.error || 'Antwort fehlgeschlagen.');
    setReplyBusy(false);
  }

  const gefiltert = useMemo(() => (filter === 'Alle' ? items : items.filter((e) => e.bereich === filter)), [items, filter]);

  if (!open) return null;

  return (
    <div className="md:hidden fixed inset-0 z-[110] flex flex-col bg-gradient-to-b from-[#0e1430] via-[#0a0f22] to-[#070a16] text-slate-100">
      <div className="flex items-center gap-3 px-4 pt-[calc(env(safe-area-inset-top)+14px)] pb-3 border-b border-white/10">
        <Code2 className="w-6 h-6 text-[#6d8bff]" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Code / Entwicklung</p>
          <p className="text-[11px] text-slate-400 truncate">{aktualisiert ? `Stand ${aktualisiert} Uhr` : 'Was gerade gebaut wird'}</p>
        </div>
        <button onClick={laden} aria-label="Aktualisieren" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-300">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <button onClick={onClose} aria-label="Schließen" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-300">
          <X className="w-5 h-5" />
        </button>
      </div>

      {summary && (
        <div className="flex gap-2 overflow-x-auto px-4 pt-3 pb-1 no-scrollbar text-[11px]">
          {summary.rueckfrage > 0 && <span className="flex-none px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300">{summary.rueckfrage} Rückfrage</span>}
          {summary.bereit > 0 && <span className="flex-none px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300">{summary.bereit} zum Merge</span>}
          {summary.review > 0 && <span className="flex-none px-2.5 py-1 rounded-full bg-[#6d8bff]/20 text-[#a9bcff]">{summary.review} Review</span>}
          {summary.warteschlange > 0 && <span className="flex-none px-2.5 py-1 rounded-full bg-white/10 text-slate-300">{summary.warteschlange} Warteschlange</span>}
          {summary.fertig > 0 && <span className="flex-none px-2.5 py-1 rounded-full bg-slate-500/20 text-slate-400">{summary.fertig} fertig (24 h)</span>}
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto px-4 py-2 border-b border-white/5 no-scrollbar">
        {['Alle', ...bereiche].map((b) => (
          <button key={b} onClick={() => setFilter(b)}
            className={`flex-none text-[11px] px-3 py-1 rounded-full border ${filter === b ? 'text-white border-transparent bg-gradient-to-br from-[#6d8bff] to-[#9d6bff]' : 'text-slate-300 border-white/15 bg-white/5'}`}>
            {b}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
        {loading && items.length === 0 && <div className="text-slate-400 text-sm flex items-center gap-2 px-1"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>}
        {!loading && gefiltert.length === 0 && <div className="text-slate-400 text-sm px-1 py-8 text-center">{hinweis || 'Aktuell wird hier nichts gebaut.'}</div>}

        {gefiltert.map((e) => {
          const key = `${e.repo}-${e.nummer}`;
          return (
            <div key={`${e.repo}-${e.art}-${e.nummer}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_STYLE[e.status] || 'bg-white/10 text-slate-300'}`}>{e.status}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-300">{e.bereich}</span>
                <span className="text-[10px] text-slate-500">{e.art} #{e.nummer}</span>
                {e.prioritaet === 'high' && <span className="text-[10px] text-rose-300">· hoch</span>}
              </div>
              <p className="text-[13.5px] font-medium text-slate-100 leading-snug">{e.titel}</p>

              <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className={`h-full rounded-full ${BAR_COLOR[e.status] || 'bg-slate-400'}`} style={{ width: `${e.fortschritt}%` }} />
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[11px] text-slate-400">{e.voraussichtlich}</span>
                <a href={e.url} target="_blank" rel="noreferrer" className="text-[10px] text-slate-500 flex items-center gap-1">{e.fortschritt}% · Öffnen <ExternalLink className="w-3 h-3" /></a>
              </div>

              {e.kann_antworten && (
                <div className="mt-2.5 pt-2.5 border-t border-white/10">
                  {replyKey !== key ? (
                    <button onClick={() => oeffneAntwort(e, key)} className="flex items-center gap-1.5 text-[12px] text-amber-300">
                      <MessageSquare className="w-4 h-4" /> Antworten (mit Vorschlägen)
                    </button>
                  ) : (
                    <div className="space-y-2.5">
                      {detailsBusy && <div className="text-[12px] text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Rückfrage wird erklärt …</div>}
                      {details && (
                        <div className="space-y-2">
                          <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-2.5">
                            <p className="text-[12px] text-amber-100"><span className="font-semibold">Der Agent fragt:</span> {details.frage}</p>
                            {details.warum && <p className="text-[11px] text-amber-200/80 mt-1"><span className="font-semibold">Warum:</span> {details.warum}</p>}
                          </div>
                          {Array.isArray(details.vorschlaege) && details.vorschlaege.length > 0 && (
                            <div className="space-y-1.5">
                              <p className="text-[11px] text-slate-400">Vorschläge (antippen übernimmt den Text):</p>
                              {[...details.vorschlaege].sort((a, b) => (b.empfohlen ? 1 : 0) - (a.empfohlen ? 1 : 0)).map((v, idx) => (
                                <button key={idx} onClick={() => setReplyText(v.text)}
                                  className={`w-full text-left text-[12px] px-3 py-2 rounded-lg border ${v.empfohlen ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-100' : 'border-white/10 bg-white/[0.03] text-slate-200'}`}>
                                  {v.empfohlen && <span className="text-[9px] uppercase tracking-wide text-emerald-300 mr-1.5">Empfohlen</span>}
                                  {v.text}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <textarea value={replyText} onChange={(ev) => setReplyText(ev.target.value)} rows={3} placeholder="Deine Antwort (oder Vorschlag antippen) …"
                        className="w-full bg-white/[0.07] border border-white/10 rounded-lg px-3 py-2 text-[13px] text-slate-100 placeholder:text-slate-500 outline-none" />
                      <div className="flex gap-2">
                        <button disabled={replyBusy || !replyText.trim()} onClick={() => antworten(e)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gradient-to-br from-[#6d8bff] to-[#9d6bff] text-white text-[12px] disabled:opacity-50">
                          {replyBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Senden
                        </button>
                        <button onClick={() => { setReplyKey(null); setReplyMsg(null); setDetails(null); }} className="px-3 py-2 rounded-lg border border-white/15 text-slate-300 text-[12px]">Abbrechen</button>
                      </div>
                      {replyMsg && <p className="text-[11px] text-amber-200">{replyMsg}</p>}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <p className="text-[10px] text-slate-500 px-1 pt-1">Zeiten sind Schätzungen (deutsche Zeit). Antworten bei „Rückfrage" gehen als Kommentar ans Issue — der Agent macht dann weiter.</p>
      </div>
    </div>
  );
}
